import { useContext, useEffect, useState, useRef } from 'react';
import { Box, Button, Flex, Heading, useDisclosure } from 'src/components/ui';

import {
	ResendInviteModal,
	UserDeleteModal,
	UserFormModal,
	UserSeatsLimitModal,
	getUsersListingColumns,
} from 'src/components/user';
import {
	DataTable,
	DataTablePagination,
	TActions,
} from 'src/components/common/DataTable';
import usePagination from 'src/hooks/usePagination';
import useToggleWithPayload from 'src/hooks/useToggleWithPayload';
import {
	deleteUser,
	getUsers,
	inviteUser,
	updateUser,
} from 'src/services/users';
import { IUser, IUserForm } from 'src/lib/schemas';
import { toastError } from 'src/services/toast';
import UserContext from 'src/contexts/user/UserContext';
import CustomContainer from 'src/components/common/CustomContainer';

interface UserSeatsLimitData {
	currentCount?: number;
	limit?: number;
	provider?: 'appsumo' | 'stripe';
	planName?: string;
	canUpgrade?: boolean;
	upgradePrice?: number;
	upgradeCurrency?: string;
	nextPlanId?: string;
}

const ManageUsers = () => {
	const pagination = usePagination();
	const [isLoading, setIsLoading] = useState(true);
	const [users, setUsers] = useState<Array<IUser>>([]);
	const formToggle = useToggleWithPayload<IUser>();
	const deleteToggle = useToggleWithPayload<IUser>();
	const resendInviteToggle = useToggleWithPayload<IUser>();
	const seatsLimitModal = useDisclosure();
	const [seatsLimitData, setSeatsLimitData] = useState<UserSeatsLimitData>({});
	const lastInvitePayloadRef = useRef<IUserForm | null>(null);

	const { user } = useContext(UserContext);
	const handleDisable = (action: TActions, row: IUser) => {
		const isSelf = user?.id === row.id;
		const hideResendInviteButton = isSelf || !row.isInvited;
		if (isSelf && action === 'Delete') {
			return true;
		} else if (hideResendInviteButton && action === 'Resend invite')
			return true;
		return false;
	};

	const handleCreateUpdateUser = async (payload: IUserForm) => {
		try {
			if (payload.id) {
				await updateUser(payload);
			} else {
				lastInvitePayloadRef.current = payload; // Save for retry after upgrade
				await inviteUser(payload);
			}
			fetchUsers();
		} catch (error: any) {
			if (error.response?.status === 402 && error.response?.data?.code === 'USER_SEATS_LIMIT_REACHED') {
				const data = error.response.data;
				setSeatsLimitData({
					currentCount: data.currentCount,
					limit: data.limit,
					provider: data.provider,
					planName: data.planName,
					canUpgrade: data.canUpgrade,
					upgradePrice: data.upgradePrice,
					upgradeCurrency: data.upgradeCurrency,
					nextPlanId: data.nextPlanId,
				});
				seatsLimitModal.onOpen();
			} else {
				throw error;
			}
		}
	};

	const handleDeleteUser = async (user: IUser) => {
		await deleteUser(user.id);
		fetchUsers();
	};

	const handleResentInvite = async (user: IUser) => {
		try {
			const { email, name, role } = user;
			const payload = { email, name, role };
			lastInvitePayloadRef.current = payload; // Save for retry after upgrade
			await inviteUser(payload);
			fetchUsers();
		} catch (error: any) {
			if (error.response?.status === 402 && error.response?.data?.code === 'USER_SEATS_LIMIT_REACHED') {
				const data = error.response.data;
				setSeatsLimitData({
					currentCount: data.currentCount,
					limit: data.limit,
					provider: data.provider,
					planName: data.planName,
					canUpgrade: data.canUpgrade,
					upgradePrice: data.upgradePrice,
					upgradeCurrency: data.upgradeCurrency,
					nextPlanId: data.nextPlanId,
				});
				seatsLimitModal.onOpen();
			} else {
				throw error;
			}
		}
	};

	const handleAction = (action: TActions, data: IUser) => {
		switch (action) {
			case 'Edit':
				formToggle.onOpen(users.find((u) => u.id === data.id));
				break;
			case 'Delete':
				deleteToggle.onOpen(users.find((u) => u.id === data.id));
				break;
			case 'Resend invite':
				resendInviteToggle.onOpen(users.find((u) => u.id === data.id));
				break;
			default:
				break;
		}
	};

	const columns = getUsersListingColumns(handleAction, handleDisable);

	const fetchUsers = async () => {
		setIsLoading(true);
		try {
			const query = `page=${pagination.page}`;
			const { results, totalPages } = await getUsers(query);
			setUsers(results);
			pagination.setPages(totalPages);
		} catch (error: any) {
			toastError(error);
		}
		setIsLoading(false);
	};

	useEffect(() => {
		fetchUsers();
	}, [pagination.page]);

	return (
		<CustomContainer>
			<Flex direction="column" gap={4}>
				<Flex
					justify="space-between"
					alignItems={{ base: 'stretch', md: 'center' }}
					direction={{ base: 'column', md: 'row' }}
					gap={{ base: 3, md: 0 }}
				>
					<Heading fontSize={{ base: 'xl', md: '2xl' }}>Manage Users</Heading>
					<Button
						onClick={() => formToggle.onOpen()}
						variant="orangeSolid"
						w={{ base: '100%', md: 'auto' }}
					>
						Invite User
					</Button>
				</Flex>
				<Box overflowX={{ base: 'auto', md: 'visible' }} w="100%">
					<DataTable
						columns={columns}
						data={users}
						isLoading={isLoading}
						tableProps={{
							border: '1px solid #F5F5F5',
							minH: '400px',
							minW: { base: '600px', md: 'auto' },
						}}
						trProps={{
							borderBottomWidth: '1px',
							borderColor: '#F5F5F5',
						}}
					/>
				</Box>
				<DataTablePagination {...pagination} />
				<UserFormModal
					isOpen={formToggle.isOpen}
					onClose={formToggle.onClose}
					onSubmit={handleCreateUpdateUser}
					title={`${formToggle.payload ? 'Edit' : 'Invite'} User`}
					initialValues={formToggle.payload}
				/>
				<UserDeleteModal
					isOpen={deleteToggle.isOpen}
					onClose={deleteToggle.onClose}
					onConfirm={handleDeleteUser}
					data={deleteToggle.payload}
				/>
				<ResendInviteModal
					isOpen={resendInviteToggle.isOpen}
					onClose={resendInviteToggle.onClose}
					onConfirm={handleResentInvite}
					data={resendInviteToggle.payload}
				/>
				<UserSeatsLimitModal
					isOpen={seatsLimitModal.isOpen}
					onClose={seatsLimitModal.onClose}
					currentCount={seatsLimitData.currentCount}
					limit={seatsLimitData.limit}
					provider={seatsLimitData.provider}
					planName={seatsLimitData.planName}
					canUpgrade={seatsLimitData.canUpgrade}
					upgradePrice={seatsLimitData.upgradePrice}
					upgradeCurrency={seatsLimitData.upgradeCurrency}
					nextPlanId={seatsLimitData.nextPlanId}
					onUpgradeSuccess={async () => {
						// Retry inviting the user after successful upgrade
						if (lastInvitePayloadRef.current) {
							try {
								await inviteUser(lastInvitePayloadRef.current);
								fetchUsers();
							} catch (error) {
								toastError(error);
							}
						}
					}}
				/>
			</Flex>
		</CustomContainer>
	);
};

export default ManageUsers;
